import { useMemo, useState } from "react";
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Paper,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import ContentCopyOutlinedIcon from "@mui/icons-material/ContentCopyOutlined";
import { toast } from "react-toastify";
import { fetchVisaSignatureImage, useVisaDocumentSignatures } from "utils/visaDocumentService";
import { copyImageToClipboard } from "utils/imageClipboard";

const SEARCH_THRESHOLD = 6;

const SignatureCard = ({ signature, onCopy, copying }) => (
  <Paper
    variant="outlined"
    data-testid="signature-card"
    className="border-app-border p-2.5 rounded-xl"
  >
    <Stack direction="row" alignItems="center" justifyContent="space-between" spacing={1}>
      <Typography variant="body2" fontWeight={700} className="min-w-0 break-words">
        {signature.label}
      </Typography>
      <Chip size="small" variant="outlined" label={signature.signer} sx={{ flexShrink: 0 }} />
    </Stack>
    <Box
      sx={{
        mt: 1,
        height: 110,
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        bgcolor: "#fff",
        border: "1px dashed",
        borderColor: "divider",
        borderRadius: 1,
      }}
    >
      {signature.imageUrl ? (
        <img
          src={signature.imageUrl}
          alt={signature.label}
          loading="lazy"
          style={{ maxWidth: "100%", maxHeight: "100%", objectFit: "contain" }}
        />
      ) : (
        <Typography variant="caption" color="text.secondary">
          Image unavailable
        </Typography>
      )}
    </Box>
    <Button
      size="small"
      variant="outlined"
      startIcon={<ContentCopyOutlinedIcon fontSize="small" />}
      onClick={() => onCopy(signature)}
      disabled={copying}
      sx={{ mt: 1 }}
    >
      Copy
    </Button>
  </Paper>
);

/**
 * Every signature on a filled document, with its label, so a long form is browsed here instead of
 * spreading its signatures across the Documents tab. Copying goes through the authenticated image
 * endpoint: the preview links point at storage, which does not allow the cross-origin read a copy
 * would need.
 */
const DocumentSignaturesDialog = ({ open, onClose, visaId, templateId, documentTitle }) => {
  const {
    data: signatures = [],
    isLoading,
    isError,
  } = useVisaDocumentSignatures(visaId, templateId, { enabled: open });
  const [query, setQuery] = useState("");
  const [copyingField, setCopyingField] = useState(null);

  const visible = useMemo(() => {
    const needle = query.trim().toLowerCase();
    if (!needle) return signatures;
    return signatures.filter((signature) =>
      `${signature.label} ${signature.signer}`.toLowerCase().includes(needle)
    );
  }, [signatures, query]);

  const handleCopy = async (signature) => {
    setCopyingField(signature.fieldName);
    try {
      await copyImageToClipboard(() =>
        fetchVisaSignatureImage(visaId, templateId, signature.fieldName)
      );
      toast.success(`${signature.label} copied`);
    } catch (err) {
      toast.error(err?.message || "Could not copy the signature");
    } finally {
      setCopyingField(null);
    }
  };

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="md">
      <DialogTitle className="font-bold">
        Signatures in {documentTitle}
        {signatures.length > 0 && (
          <Typography component="span" variant="body2" color="text.secondary" sx={{ ml: 1 }}>
            ({signatures.length})
          </Typography>
        )}
      </DialogTitle>
      <DialogContent dividers>
        {isLoading && (
          <Stack alignItems="center" sx={{ py: 4 }}>
            <CircularProgress size={24} />
          </Stack>
        )}
        {isError && <Alert severity="error">Could not load the signatures.</Alert>}
        {!isLoading && !isError && signatures.length === 0 && (
          <Alert severity="info">No signatures on this document.</Alert>
        )}
        {signatures.length > SEARCH_THRESHOLD && (
          <TextField
            size="small"
            fullWidth
            placeholder="Search signatures"
            value={query}
            onChange={(event) => setQuery(event.target.value)}
            sx={{ mb: 1.5 }}
          />
        )}
        {signatures.length > 0 && visible.length === 0 && (
          <Typography variant="body2" color="text.secondary">
            No signature matches your search.
          </Typography>
        )}
        <Box
          sx={{
            display: "grid",
            gap: 1.5,
            gridTemplateColumns: { xs: "1fr", sm: "repeat(2, 1fr)" },
          }}
        >
          {visible.map((signature) => (
            <SignatureCard
              key={signature.fieldName}
              signature={signature}
              onCopy={handleCopy}
              copying={copyingField === signature.fieldName}
            />
          ))}
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Close</Button>
      </DialogActions>
    </Dialog>
  );
};

export default DocumentSignaturesDialog;
